import { useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import { useNotify } from 'ra-core'
import { DynamicFormRenderer } from '@internal/ui-kit/components/app/DynamicFormRenderer'
import type { DynamicFormSpec } from '@internal/ui-kit/components/app/DynamicFormRenderer'
import { Button } from '@internal/ui-kit/components/ui/button'
import { Checkbox } from '@internal/ui-kit/components/ui/checkbox'
import { Combobox, MultiCombobox } from '@internal/ui-kit/components/ui/combobox'
import { Label } from '@internal/ui-kit/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@internal/ui-kit/components/ui/select'
import { Switch } from '@internal/ui-kit/components/ui/switch'
import { extractErrorMessage, httpClient } from '@internal/ui-kit/lib/httpClient'
import { useCartFormLinks, useCartProduct, useCartProductSearch, useCartSlots } from './useCart'

type BookingDetailsDefaults = {
  first_name?: string
  last_name?: string
  email?: string
  phone_number?: string
  date_of_birth?: string
  gender?: string
}

/**
 * The fallback tier of the booking-details form resolution (a
 * `form-links`-linked form, else this — `product.form_id` is deprecated,
 * per review, so it's no longer a tier at all) — matches
 * MIGRATION-NOTES.md's "Step 3" fixed field set (no true
 * dynamic-JSON-Schema-driven form existed anywhere in this codebase when
 * that ticket shipped it), just now expressed as a `DynamicFormSpec` so it
 * renders through the same `DynamicFormRenderer` a real linked form does
 * (HATCH-453 — "respect linked form and render linked form vs default
 * form"), instead of its own bespoke one-off field list.
 */
const DEFAULT_BOOKING_FORM: DynamicFormSpec = {
  form_id: 'default-booking-details',
  slug: 'booking-details',
  label: 'Booking details',
  show_header: false,
  fields: [
    { field_name: 'first_name', label: 'First name', field_type: 'text' },
    { field_name: 'last_name', label: 'Last name', field_type: 'text' },
    { field_name: 'email', label: 'Email', field_type: 'email' },
    { field_name: 'phone_number', label: 'Phone number', field_type: 'text' },
    { field_name: 'date_of_birth', label: 'Date of birth', field_type: 'date' },
    { field_name: 'gender', label: 'Gender', field_type: 'text' },
    { field_name: 'reason', label: 'Reason', field_type: 'text' },
  ],
}

/** Same shape as `ManageUserCartDialog.tsx`'s own `useDebouncedValue` (a server-filtered `Combobox`'s search box) — the product picker here drove `useCartProductSearch` off the raw keystroke value instead (ticket: "no debounce when searching product"). 300ms, not that convention's 750ms — ticket feedback: "lets do debounce to 300ms instead". */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])
  return debounced
}

function scheduleLabel(schedule: { location_type?: string | null; branch?: { title?: string | null } | null }): string {
  const caption = schedule.branch?.title ?? '—'
  return schedule.location_type ? `${schedule.location_type.toUpperCase()} — ${caption}` : caption
}

function slotLabel(slot: { start_time: string; end_time: string }): string {
  const start = DateTime.fromISO(slot.start_time, { zone: 'utc' }).toLocal()
  const end = DateTime.fromISO(slot.end_time, { zone: 'utc' }).toLocal()
  return `${start.toLocaleString(DateTime.DATETIME_MED)} – ${end.toLocaleString(DateTime.TIME_SIMPLE)}`
}

/**
 * The product/option/schedule/slot/booking-details sub-flow (HATCH-149,
 * `chooseProductSelect`/`select2`/`select3`/`switch1ScheduleLater`/
 * `multiselect1`/`jsonSchemaForm1`/`checkboxAllowInventory` — see
 * MIGRATION-NOTES.md's "Step 3"), rendered inline inside
 * `ManageUserCartDialog` rather than its own sheet, matching the source
 * export's own nested (not separately modal) sub-flow.
 */
export function AddCartItemPanel({
  userId,
  bookingDetailsDefaults,
  onAdded,
  onCancel,
}: {
  userId: string
  bookingDetailsDefaults: BookingDetailsDefaults
  onAdded: () => void
  onCancel: () => void
}) {
  const notify = useNotify()
  const [productQuery, setProductQuery] = useState('')
  const debouncedProductQuery = useDebouncedValue(productQuery, 300)
  const [productId, setProductId] = useState('')
  const [optionId, setOptionId] = useState('')
  const [scheduleLater, setScheduleLater] = useState(false)
  const [scheduleId, setScheduleId] = useState('')
  const [selectedSlotIndexes, setSelectedSlotIndexes] = useState<string[]>([])
  const [allowInventory, setAllowInventory] = useState(false)
  const [bookingValues, setBookingValues] = useState<Record<string, string>>({})
  const [bookingArrayValues, setBookingArrayValues] = useState<Record<string, string[]>>({})
  const [isSaving, setIsSaving] = useState(false)

  const { products } = useCartProductSearch(debouncedProductQuery)
  const { product } = useCartProduct(productId || undefined)
  const options = product?.options ?? []
  const selectedOption = options.find((option) => option.option_id === optionId)
  const schedules = selectedOption?.schedules ?? []
  const selectedSchedule = schedules.find((schedule) => schedule.schedule_id === scheduleId)
  const effectiveScheduleId = !scheduleLater ? scheduleId || undefined : undefined
  const effectiveBranchId = !scheduleLater ? (selectedSchedule?.branch_id ?? undefined) : undefined
  const { slots } = useCartSlots(productId || undefined, effectiveScheduleId)

  // The booking-details form resolution, ported from `marketplace-frontend`'s
  // own `BookScheduleForm.jsx`/`BookingDetailsFormView.jsx` (confirmed via
  // `ref-repo.sh`, not guessed — round 5 guessed a "linked form must extend
  // the default set" rule that turned out wrong; this replaces it):
  // `product.needs_booking_details` wins outright and always renders the
  // fixed default set, ignoring any linked form entirely (that source's own
  // `if (product.needs_booking_details) return true` / `BookingDetailsForm`
  // branches, both checked *before* looking at `form` at all). Otherwise the
  // linked form is used exactly as returned, with no shape requirement on it
  // — that source's own `!form.form_id ? null : ...` / `BookingDetailsDynamicForm`
  // branch. The source also skips a `pure_contact_form` linked form when
  // `bookingForMyself` (the customer's own self-checkout flow already has
  // their contact info) — this admin flow has no "myself" concept (the admin
  // is never the patient), so that exception can't fire here and is omitted.
  const { formLinks } = useCartFormLinks(productId || undefined, product?.section_id ?? undefined, effectiveScheduleId, effectiveBranchId)
  const linkedForm = formLinks[0]?.form
  const bookingForm = product?.needs_booking_details ? DEFAULT_BOOKING_FORM : linkedForm

  // Every dependency of the slots-per-product-per-schedule triple resets
  // downstream selections, matching the source's own cascading
  // `resetValue` events on `chooseProductSelect`/`select2`/`select3`.
  useEffect(() => {
    setOptionId('')
    setScheduleId('')
    setSelectedSlotIndexes([])
  }, [productId])
  useEffect(() => {
    setScheduleId('')
    setSelectedSlotIndexes([])
  }, [optionId])
  useEffect(() => {
    setSelectedSlotIndexes([])
  }, [scheduleId, scheduleLater])

  // Re-seeds whenever the *resolved* form changes identity (a different
  // linked form, the product's own form, or the default set) — not on every
  // keystroke, matching `UserDynamicFormPanel`'s own re-seed-on-data-change
  // convention.
  useEffect(() => {
    if (!bookingForm) return
    const defaults: Record<string, string> = {
      first_name: bookingDetailsDefaults.first_name ?? '',
      last_name: bookingDetailsDefaults.last_name ?? '',
      email: bookingDetailsDefaults.email ?? '',
      phone_number: bookingDetailsDefaults.phone_number ?? '',
      date_of_birth: bookingDetailsDefaults.date_of_birth ?? '',
      gender: bookingDetailsDefaults.gender ?? '',
    }
    setBookingValues(Object.fromEntries(bookingForm.fields.map((field) => [field.field_name, defaults[field.field_name] ?? ''])))
    setBookingArrayValues({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingForm?.form_id])

  const needsBookingDetails = Boolean(bookingForm)
  // `select3.selectedItem.slots_number` (the schedule's own field, not the
  // option's — see `schema.ts`'s `cartProductOptionSchema` comment).
  const maxSlots = selectedSchedule?.slots_number ?? 0
  const hasSchedules = schedules.length > 0
  const slotsSatisfied = scheduleLater || maxSlots === 0 || selectedSlotIndexes.length === maxSlots

  // Ticket feedback (HATCH-519): a product with no schedules at all (e.g. a
  // physical product) never shows the "Schedule later" switch or a schedule
  // picker (both gated on `hasSchedules` below) — `scheduleLater` and
  // `scheduleId` then stay at their unset defaults forever, which used to
  // leave `canSubmit` permanently `false` for exactly this kind of product,
  // with no way to add it to the cart at all. `!hasSchedules` short-circuits
  // that case the same way `scheduleLater` already does.
  const canSubmit = Boolean(productId && optionId && (!hasSchedules || scheduleLater || (scheduleId && slotsSatisfied)))

  const submit = async () => {
    if (!canSubmit) return
    setIsSaving(true)
    try {
      const selectedSlots = selectedSlotIndexes.map((index) => slots[Number(index)]).filter(Boolean)
      // Ticket feedback: a linked form that omits date_of_birth/gender/reason
      // must still send those keys explicitly as `null` (not simply absent) —
      // `bookingValues` only has entries for whatever `bookingForm.fields`
      // resolved to, so these three are seeded `null` first and overridden
      // by `bookingValues` when the resolved form does define them.
      const detailsPayload = needsBookingDetails
        ? { date_of_birth: null, gender: null, reason: null, ...bookingValues, ...bookingArrayValues }
        : null
      await httpClient(`/admin/orders/by-user/${userId}/current`, {
        method: 'POST',
        body: JSON.stringify({
          product_id: productId,
          option_id: optionId,
          // `scheduleId` is `''` (never set), not `null`, for a
          // schedule-less product — the real `CreateOrderRequest.schedule_id`
          // is `uuid | null`, so an empty string must still normalize to
          // `null` rather than going out as an invalid value.
          schedule_id: scheduleLater ? null : scheduleId || null,
          schedule_later: scheduleLater,
          slots: scheduleLater ? [] : selectedSlots,
          use_user_inventory: allowInventory,
          booking_details: !scheduleLater ? detailsPayload : null,
          // Ticket feedback: "schedule_later can't have booking details" —
          // this used to gate on `scheduleLater` (sending `detailsPayload`
          // only when scheduling later, the opposite of `booking_details`
          // above), so a scheduled-later item never got either field set.
          // Same `!scheduleLater` condition as `booking_details`.
          additional_booking_details: !scheduleLater ? detailsPayload : null,
        }),
      })
      notify('Item added to cart.', { type: 'info' })
      onAdded()
    } catch (err) {
      notify(extractErrorMessage(err, 'Could not add this item to the cart.'), { type: 'error' })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-4 rounded-md border p-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="cart-item-product">Product</Label>
        <Combobox
          id="cart-item-product"
          options={products.map((item) => ({ value: item.product_id, label: item.title ?? item.product_id }))}
          value={productId}
          onChange={(value) => {
            setProductId(value)
            setProductQuery('')
          }}
          placeholder="Search products"
          searchValue={productQuery}
          onSearchChange={setProductQuery}
          searchPlaceholder="Type to search…"
          className="w-full"
        />
      </div>

      {options.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cart-item-option">Option</Label>
          <Select value={optionId} onValueChange={setOptionId}>
            <SelectTrigger id="cart-item-option">
              <SelectValue placeholder="Choose an option" />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.option_id} value={option.option_id}>
                  {option.title ?? option.option_id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {optionId && hasSchedules ? (
        <div className="flex items-center justify-between">
          <Label htmlFor="cart-item-schedule-later">Schedule later</Label>
          <Switch id="cart-item-schedule-later" checked={scheduleLater} onCheckedChange={setScheduleLater} />
        </div>
      ) : null}

      {optionId && hasSchedules && !scheduleLater ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cart-item-schedule">Schedule</Label>
          <Select value={scheduleId} onValueChange={setScheduleId}>
            <SelectTrigger id="cart-item-schedule">
              <SelectValue placeholder="Choose a schedule" />
            </SelectTrigger>
            <SelectContent>
              {schedules.map((schedule) => (
                <SelectItem key={schedule.schedule_id} value={schedule.schedule_id}>
                  {scheduleLabel(schedule)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {selectedSchedule && !scheduleLater && maxSlots > 0 ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cart-item-slots">{`Slot (max ${maxSlots} slot${maxSlots === 1 ? '' : 's'})`}</Label>
          <MultiCombobox
            id="cart-item-slots"
            options={slots.map((slot, index) => ({ value: String(index), label: slotLabel(slot) }))}
            value={selectedSlotIndexes}
            onChange={(next) => setSelectedSlotIndexes(next.slice(0, maxSlots))}
            placeholder={slots.length === 0 ? 'No available slots' : 'Select slots'}
          />
        </div>
      ) : null}

      {bookingForm && !scheduleLater && slotsSatisfied ? (
        <div className="flex flex-col gap-3">
          <Label>Booking details</Label>
          <DynamicFormRenderer
            form={bookingForm}
            values={bookingValues}
            onChange={(fieldName, value) => setBookingValues((prev) => ({ ...prev, [fieldName]: value }))}
            arrayValues={bookingArrayValues}
            onArrayChange={(fieldName, value) => setBookingArrayValues((prev) => ({ ...prev, [fieldName]: value }))}
            disabled={false}
            columns={1}
          />
        </div>
      ) : null}

      <div className="flex items-center justify-between">
        <Label htmlFor="cart-item-allow-inventory">Allow inventory</Label>
        <Checkbox
          id="cart-item-allow-inventory"
          checked={allowInventory}
          onCheckedChange={(checked) => setAllowInventory(checked === true)}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" disabled={!canSubmit || isSaving} onClick={submit}>
          Add to order
        </Button>
      </div>
    </div>
  )
}
